import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users as UsersIcon, CreditCard, Check, Trash2, Bell, Network } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { orgCanAccessFederation } from '../utils/productWorkspace';

const TABS = [
  { key: 'profile', label: 'Organization', icon: Building2 },
  { key: 'team',    label: 'Team members', icon: UsersIcon },
  { key: 'billing', label: 'Billing',      icon: CreditCard },
  { key: 'notifications', label: 'Notifications', icon: Bell },
];

const NOTIFICATION_PREF_LABELS = [
  { key: 'reportComplete', label: 'Report complete' },
  { key: 'reportFailed', label: 'Report failed' },
  { key: 'actionDue', label: 'Action due' },
  { key: 'reviewDue', label: 'Review due' },
  { key: 'invitationAccepted', label: 'Invitation accepted' },
  { key: 'federationActionOverdue', label: 'Federation action overdue' },
  { key: 'federationReviewDue', label: 'Federation review due' },
  { key: 'federationUnresolvedFindings', label: 'Federation unresolved findings' },
  { key: 'federationPeriodClosing', label: 'Federation period closing' },
  { key: 'federationReportReady', label: 'Federation report ready' },
];

// Canonical 3-level access model
const ACCESS_LEVELS = {
  owner:  'Owner',
  staff:  'Staff',
  viewer: 'Viewer',
};

// Football job titles (display only never controls authorization)
const JOB_TITLE_OPTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Analyst',
  'Sporting Director',
  'Goalkeeping Coach',
  'Medical Staff',
  'Other',
];

// ─── Organization tab ──────────────────────────────────────────────────────────
function ProfileTab() {
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.getOrganization().then(({ organization }) => setForm(organization)).catch((e) => setError(e.message));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.updateOrganization({
        name: form.name, city: form.city, website: form.website, country: form.country,
        primaryContactName: form.primaryContactName, primaryContactEmail: form.primaryContactEmail,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(e.message || 'Could not save changes'); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-gray-400 text-sm">Loading…</p>;

  return (
    <form onSubmit={handleSave} className="max-w-xl space-y-5">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      {saved && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2"><Check className="w-4 h-4" /> Saved</div>}
      <div>
        <label className="block text-sm font-medium text-navy-900 mb-1.5">Organization name</label>
        <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
      </div>
      <div>
        <label className="block text-sm font-medium text-navy-900 mb-1.5">Type</label>
        <input value={form.organizationType || ''} disabled
          className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-lg px-4 py-2.5" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy-900 mb-1.5">Country</label>
          <input value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy-900 mb-1.5">City</label>
          <input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-navy-900 mb-1.5">Website</label>
        <input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="https://"
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy-900 mb-1.5">Primary contact name</label>
          <input value={form.primaryContactName || ''} onChange={(e) => setForm({ ...form, primaryContactName: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy-900 mb-1.5">Primary contact email</label>
          <input value={form.primaryContactEmail || ''} onChange={(e) => setForm({ ...form, primaryContactEmail: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400" />
        </div>
      </div>
      <button type="submit" disabled={saving}
        className="bg-navy-900 hover:bg-navy-800 disabled:opacity-60 text-white font-medium px-6 py-2.5 rounded transition-colors">
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

// ─── Job title field ───────────────────────────────────────────────────────────
function JobTitleField({ value, onChange }) {
  const isOther = value && !JOB_TITLE_OPTIONS.slice(0, -1).includes(value);
  const selectVal = isOther ? 'Other' : (value || '');

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-gray-500 font-medium">Job title <span className="font-normal">(optional)</span></label>
      <select
        value={selectVal}
        onChange={(e) => onChange(e.target.value === 'Other' ? '' : e.target.value)}
        className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full focus:outline-none focus:border-navy-400"
      >
        <option value=""> select </option>
        {JOB_TITLE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {(selectVal === 'Other' || isOther) && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter job title"
          maxLength={100}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-navy-400"
        />
      )}
    </div>
  );
}

// ─── Team members tab ─────────────────────────────────────────────────────────
function TeamTab() {
  const { user } = useAuth();
  const canManage = user?.role === 'owner';

  const [users, setUsers]         = useState([]);
  const [teams, setTeams]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [inviting, setInviting]   = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'staff', job_title: '' });

  const load = () => {
    Promise.all([api.getUsers(), api.getTeams().catch(() => ({ teams: [] }))])
      .then(([u, t]) => { setUsers(u.users || []); setTeams(t.teams || []); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleTeam = async (u, teamId) => {
    if (!canManage) return;
    const current = new Set(u.team_ids || []);
    if (current.has(teamId)) current.delete(teamId); else current.add(teamId);
    try {
      await api.assignUserTeams({ userId: u.id, teamIds: [...current] });
      load();
    } catch (e) { setError(e.message || 'Could not update team access'); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true); setError('');
    try {
      await api.inviteUser(inviteForm);
      setInviteForm({ email: '', name: '', role: 'staff', job_title: '' });
      load();
    } catch (e) { setError(e.message || 'Could not send invite'); }
    finally { setInviting(false); }
  };

  const changeAccess = async (userId, role) => {
    try {
      await api.updateUser({ userId, role });
      load();
    } catch (e) { setError(e.message || 'Could not update access level'); }
  };

  const changeJobTitle = async (userId, job_title) => {
    try {
      await api.updateUser({ userId, job_title });
      load();
    } catch (e) { setError(e.message || 'Could not update job title'); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.updateUser({ userId: u.id, status: u.status === 'Deactivated' ? 'Active' : 'Deactivated' });
      load();
    } catch (e) { setError(e.message || 'Could not update status'); }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;

  return (
    <div className="max-w-4xl">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
          <button className="ml-3 underline text-xs" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {/* User table */}
      <div className="border border-gray-200 rounded-xl overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Access level</th>
              <th className="text-left px-4 py-3">Job title</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Team access</th>
              {canManage && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((u) => (
              <tr key={u.id} className={u.status === 'Deactivated' ? 'opacity-50' : ''}>
                <td className="px-4 py-3">
                  <p className="text-navy-900 font-medium">{u.name || u.email}</p>
                  <p className="text-gray-400 text-xs">{u.email}</p>
                </td>

                {/* Access level selector */}
                <td className="px-4 py-3">
                  {canManage && u.id !== user?.id ? (
                    <select
                      value={u.role}
                      onChange={(e) => changeAccess(u.id, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-navy-400"
                    >
                      {Object.entries(ACCESS_LEVELS).map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      u.role === 'owner' ? 'bg-navy-100 text-navy-800' :
                      u.role === 'staff' ? 'bg-blue-50 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {ACCESS_LEVELS[u.role] || u.role}
                    </span>
                  )}
                </td>

                {/* Job title decorative only */}
                <td className="px-4 py-3 min-w-[160px]">
                  {canManage ? (
                    <JobTitleField
                      value={u.job_title || ''}
                      onChange={(v) => changeJobTitle(u.id, v)}
                    />
                  ) : (
                    <span className="text-gray-500 text-sm">{u.job_title || ''}</span>
                  )}
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    u.status === 'Active'  ? 'bg-green-50 text-green-700' :
                    u.status === 'Invited' ? 'bg-amber-50 text-amber-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>{u.status}</span>
                </td>

                {/* Team access */}
                <td className="px-4 py-3">
                  {u.role === 'owner' ? (
                    <span className="text-xs text-gray-400">All teams</span>
                  ) : teams.length === 0 ? (
                    <span className="text-xs text-gray-400">No teams yet</span>
                  ) : canManage ? (
                    <div className="flex flex-wrap gap-1">
                      {teams.map((t) => {
                        const on = (u.team_ids || []).includes(t.id);
                        return (
                          <button key={t.id} type="button" onClick={() => toggleTeam(u, t.id)}
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                              on ? 'bg-navy-900 text-white border-navy-900'
                                 : 'bg-white text-gray-500 border-gray-300 hover:border-navy-400'
                            }`}>
                            {t.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500">{(u.team_ids || []).length} team(s)</span>
                  )}
                </td>

                {/* Deactivate */}
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {u.id !== user?.id && (
                      <button
                        onClick={() => toggleStatus(u)}
                        title={u.status === 'Deactivated' ? 'Reactivate' : 'Deactivate'}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite form */}
      {canManage && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-navy-900 font-semibold mb-4 text-sm">Invite a team member</h3>
          <form onSubmit={handleInvite} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Email *</label>
              <input type="email" required value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Name</label>
              <input type="text" value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Access level</label>
              <select value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy-400">
                {Object.entries(ACCESS_LEVELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <JobTitleField
                value={inviteForm.job_title}
                onChange={(v) => setInviteForm({ ...inviteForm, job_title: v })}
              />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={inviting || !inviteForm.email}
                className="bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
                {inviting ? 'Sending invite…' : 'Send invite'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── Billing tab ───────────────────────────────────────────────────────────────
function BillingTab() {
  const { user, org } = useAuth();
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  if (user?.role !== 'owner') {
    return (
      <div className="max-w-xl">
        <p className="text-gray-500 text-sm">Only an Owner can manage billing.</p>
      </div>
    );
  }

  const handleUpgrade = async (plan) => {
    if (plan === 'enterprise') {
      window.location.href = '/contact?plan=federation&from=settings';
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await api.createCheckoutSession({ plan, cycle: billingCycle });
      if (res.url) window.location.href = res.url;
      else setError(res.error || 'Could not start checkout');
    } catch (e) { setError(e.message || 'Could not start checkout'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
      {!org?.canAccessPostMatch ? (
        <div className="border border-navy-100 bg-navy-50 rounded-lg p-4 text-sm text-navy-900">
          Federation is managed through your custom annual agreement. Contact support for contract or billing changes.
        </div>
      ) : <>
      <p className="text-gray-500 text-sm mb-4">Select a Post-Match plan and billing cycle.</p>
      <div className="flex gap-2 mb-6">
        {['monthly', 'annual'].map((cycle) => (
          <button key={cycle} type="button" onClick={() => setBillingCycle(cycle)}
            className={`px-4 py-2 rounded border text-sm capitalize ${billingCycle === cycle ? 'bg-navy-900 text-white border-navy-900' : 'border-gray-300 text-navy-900'}`}>
            {cycle}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3">
        {/* Plan list sourced from canonical /api/plans populated via parent-level fetch */}
        {(window.__tactmarkPlans || [
          { key: 'coach',      label: 'Coach',                  price: '$99 / month',    note: '1 user · 1 team · 8 reports/month' },
          { key: 'club',       label: 'Club',                   price: '$399 / month',   note: 'Up to 5 users · 1 team · 30 reports/month' },
          { key: 'enterprise', label: 'Tactmark Federation', price: 'Custom annual agreement', note: 'Separate product · contract-defined access and support', contact: true },
        ]).map((p) => (
          <button key={p.key} disabled={loading} onClick={() => handleUpgrade(p.key)}
            className="flex items-center justify-between bg-white border border-gray-200 hover:border-navy-400 rounded-xl px-5 py-4 text-left transition-colors disabled:opacity-50">
            <span className="font-medium text-navy-900">{p.label}</span>
            <span className="text-gray-500 text-sm">{p.price}</span>
          </button>
        ))}
      </div>
      </>}
    </div>
  );
}

// ─── Notifications tab ─────────────────────────────────────────────────────────
function NotificationsTab() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getNotificationPreferences()
      .then((res) => setPrefs(res.preferences || {}))
      .catch((e) => setError(e.message || 'Could not load notification preferences'));
  }, []);

  const toggle = (key) => {
    setPrefs((p) => {
      if (!p) return p;
      return { ...p, [key]: !p[key] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!prefs) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await api.updateNotificationPreferences(prefs);
      setPrefs(res.preferences || prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message || 'Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs && !error) return <p className="text-gray-400 text-sm">Loading…</p>;
  if (!prefs) {
    return (
      <div className="max-w-xl">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="max-w-xl space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      {saved && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
          <Check className="w-4 h-4" /> Saved
        </div>
      )}
      <p className="text-gray-500 text-sm mb-2">
        Save which email notifications you want for this organization. Enabled categories are delivered by email when a recipient address is available.
      </p>
      <div className="space-y-2">
        {NOTIFICATION_PREF_LABELS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-3 text-sm text-navy-900">
            <input
              type="checkbox"
              checked={!!prefs[key]}
              onChange={() => toggle(key)}
              className="rounded border-gray-300 text-navy-900 focus:ring-navy-200"
            />
            {label}
          </label>
        ))}
      </div>
      <button type="submit" disabled={saving}
        className="bg-navy-900 hover:bg-navy-800 disabled:opacity-60 text-white font-medium px-6 py-2.5 rounded transition-colors">
        {saving ? 'Saving…' : 'Save preferences'}
      </button>
    </form>
  );
}

// ─── Root Settings page ────────────────────────────────────────────────────────
export default function Settings() {
  const [tab, setTab] = useState('profile');
  const { org } = useAuth();
  const showFederation = orgCanAccessFederation(org) || !!org?.canAccessFederation;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-navy-900 mb-6">Settings</h1>

      {showFederation && (
        <div className="mb-6 flex items-center gap-3 border border-navy-100 bg-navy-50 rounded-lg px-4 py-3">
          <Network className="w-4 h-4 text-navy-700 flex-shrink-0" />
          <div className="flex-1 text-sm text-navy-900">
            Manage Federation programs, templates, and reporting periods.
          </div>
          <Link
            to="/federation/settings"
            className="text-sm font-medium text-navy-900 underline hover:text-navy-700"
          >
            Federation settings
          </Link>
        </div>
      )}

      <div className="flex gap-1 border-b border-gray-200 mb-8 flex-wrap">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              tab === key
                ? 'text-navy-900 border-b-2 border-navy-900 -mb-px'
                : 'text-gray-500 hover:text-navy-700'
            }`}>
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'team'    && <TeamTab />}
      {tab === 'billing' && <BillingTab />}
      {tab === 'notifications' && <NotificationsTab />}
    </div>
  );
}
